/**
 * Allocation-free transform helpers for the M2 instanced views (D-014): a column-major TRS matrix written straight
 * into an instance-matrix array (the layout three's `Matrix4.compose` produces), and seed-derived spin
 * orientations so a chunk or fragment keeps its look from frame to frame without any per-instance state.
 */
import type { Quat } from '../../core/math'

const TAU = Math.PI * 2

/**
 * Writes the column-major matrix of translation (tx, ty, tz), unit rotation (qx, qy, qz, qw) and scale
 * (sx, sy, sz) into `m` at `o` (16 floats), like `Matrix4.compose`.
 */
export function writeTRS(
  m: Float32Array,
  o: number,
  tx: number,
  ty: number,
  tz: number,
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  sx: number,
  sy: number,
  sz: number,
): void {
  const x2 = qx + qx
  const y2 = qy + qy
  const z2 = qz + qz
  const xx = qx * x2
  const xy = qx * y2
  const xz = qx * z2
  const yy = qy * y2
  const yz = qy * z2
  const zz = qz * z2
  const wx = qw * x2
  const wy = qw * y2
  const wz = qw * z2
  m[o] = (1 - (yy + zz)) * sx
  m[o + 1] = (xy + wz) * sx
  m[o + 2] = (xz - wy) * sx
  m[o + 3] = 0
  m[o + 4] = (xy - wz) * sy
  m[o + 5] = (1 - (xx + zz)) * sy
  m[o + 6] = (yz + wx) * sy
  m[o + 7] = 0
  m[o + 8] = (xz + wy) * sz
  m[o + 9] = (yz - wx) * sz
  m[o + 10] = (1 - (xx + yy)) * sz
  m[o + 11] = 0
  m[o + 12] = tx
  m[o + 13] = ty
  m[o + 14] = tz
  m[o + 15] = 1
}

function fract(v: number): number {
  return v - Math.floor(v)
}

/**
 * A unit axis derived from `seed` in [0, 1) (uniform-ish over the sphere) into `out.x/y/z`, and the rotation of
 * `angle` radians about it plus a seed-derived phase into `out` as a unit quaternion.
 */
export function seedSpin(seed: number, angle: number, out: Quat): Quat {
  const theta = fract(seed * 7.13) * TAU
  const cosPhi = 2 * fract(seed * 13.37 + 0.5) - 1
  const sinPhi = Math.sqrt(1 - cosPhi * cosPhi)
  const ax = sinPhi * Math.cos(theta)
  const ay = sinPhi * Math.sin(theta)
  const az = cosPhi
  const half = 0.5 * (angle + seed * TAU)
  const s = Math.sin(half)
  out.x = ax * s
  out.y = ay * s
  out.z = az * s
  out.w = Math.cos(half)
  return out
}

/** A value in [lo, hi] picked by `seed` in [0, 1) with a second hash, so it is independent of `seedSpin`'s axis. */
export function seedRange(seed: number, lo: number, hi: number): number {
  return lo + (hi - lo) * fract(seed * 31.71 + 0.25)
}
